// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { clearTelemetry, useTelemetry } from "@/lib/debug-mode";
import { cn } from "@/lib/utils";

interface TelemetryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TelemetryDialog({ open, onOpenChange }: TelemetryDialogProps) {
  const { t } = useTranslation("back-office");
  const telemetry = useTelemetry();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!w-[60vw] !max-w-[60vw] max-h-[80vh] flex flex-col" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{t("commandPalette.telemetry")}</DialogTitle>
        </DialogHeader>
        {telemetry.length > 0 && (
          <div className="flex justify-start">
            <Button
              variant="ghost"
              size="sm"
              className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
              onClick={clearTelemetry}
            >
              {t("commandPalette.telemetryClearAll")}
            </Button>
          </div>
        )}
        <div className="overflow-y-auto divide-y font-mono text-xs">
          {telemetry.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">{t("commandPalette.telemetryEmpty")}</p>
          ) : (
            telemetry.map((r) => (
              <div key={r.id} className="flex items-center gap-3 py-2">
                <span
                  className={cn(
                    "w-14 shrink-0 inline-flex items-center justify-center rounded px-1.5 py-0.5 text-[10px] font-medium",
                    {
                      "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300": r.method === "GET",
                      "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300": r.method === "POST",
                      "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300":
                        r.method === "PUT" || r.method === "PATCH",
                      "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300": r.method === "DELETE",
                      "bg-muted text-muted-foreground": !["GET", "POST", "PUT", "PATCH", "DELETE"].includes(r.method),
                    },
                  )}
                >
                  {r.method}
                </span>
                <span className="flex-1 truncate text-muted-foreground">
                  {r.pathname}
                  {r.search}
                </span>
                <span className={r.status >= 400 || r.status === 0 ? "text-destructive font-medium" : ""}>
                  {r.status === 0 ? "ERR" : r.status}
                </span>
                <span className="w-14 text-right text-muted-foreground shrink-0">
                  {r.duration !== null ? `${r.duration}ms` : "—"}
                </span>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
